import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Send, X } from "lucide-react";
import { toast } from "sonner";
import { chatApi } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { usePanelOpen } from "@/components/floatingPanel";
import { onChatOpen } from "@/features/chat/chatBus";
import { apiErrorMessage } from "@/lib/apiErrors";
import { messageStamp } from "@/lib/chatStamp";

/**
 * S-2 — ephemeral staff chat. A calm floating launcher (sibling to Mira), opening a partial
 * panel with a conversation list + an active thread. POLLING, not websockets: the open thread
 * polls GET .../messages every ~2.5s; the conversation list polls slower (~8s). Everything is
 * org-scoped + participant-gated on the backend — the FE only renders the authorized view.
 *
 * Ephemeral: there is no history/search surface — only the recent window the backend returns.
 */
const THREAD_POLL_MS = 2500;
const LIST_POLL_MS = 8000;

// HEB-2: t threaded in (module helper). Real participant names/titles are server data (returned
// as-is); only the two empty fallbacks localize.
function conversationLabel(conv, myId, t) {
  if (conv.is_group) {
    if (conv.title) return conv.title;
    const others = (conv.participants || []).filter((p) => p.id !== myId).map((p) => p.name);
    return others.length ? others.join(", ") : t("chat.groupConversationFallback");
  }
  const other = (conv.participants || []).find((p) => p.id !== myId);
  return other ? other.name : t("chat.conversationFallback");
}

export function ChatLauncher() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const myId = user?.id;
  // W26 — so a targeted open can refresh the list it is about to jump past.
  const queryClient = useQueryClient();
  // TEACH-UX P1-a — open state is the provider's (usePanelOpen); the shape is unchanged, and a
  // standalone ChatLauncher still drives itself on local state.
  const [open, setOpen] = usePanelOpen("chat");
  const [view, setView] = useState("list"); // "list" | "new" | "thread"
  const [activeId, setActiveId] = useState(null);
  const panelRef = useRef(null);

  // TEACH-UX P1-a — the floating chat BUTTON is retired (three stacked FABs became one). The panel
  // now opens on request from the teacher avatar menu, via a bus that mirrors the calendar's.
  //
  // THE GATE DID NOT MOVE. `if (!myId) return null` below is unchanged and still runs before any
  // panel renders, so a request that arrives for an unauthenticated viewer opens nothing. The bus
  // carries a REQUEST; this component still decides.
  /**
   * W26 / C-257 — a request may name WHO to open, not just "open".
   *
   * `createConversation` is the right call for this and not a wasteful one: the backend REUSES an
   * existing 1:1 for the same pair (`find_direct_conversation`), so asking twice opens the same
   * thread rather than making a second one. That is why there is no "find the conversation first"
   * step here — the server already owns that decision, and duplicating it in the client is how the
   * two start disagreeing.
   *
   * A FAILED OPEN FALLS BACK TO THE LIST rather than to nothing: the panel is open either way, and
   * the admin can reach her by name. An unauthenticated viewer is still refused by `!myId` below.
   */
  useEffect(
    () =>
      onChatOpen((options) => {
        setOpen(true);
        const withUserId = options?.withUserId;
        if (!withUserId) return;
        chatApi
          .createConversation({ participant_ids: [withUserId] })
          .then((res) => {
            const conv = res?.data;
            if (!conv?.id) return;
            queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
            setActiveId(conv.id);
            setView("thread");
          })
          .catch((error) => {
            // ⚑ F-64 (C-401) — THE FAILURE IS SAID OUT LOUD, and its silence was the whole defect.
            // "Message is present but opens nothing" is what this catch looked like from the
            // outside: `POST /api/chat/conversations` answers 400 when a participant is not an
            // approved member of the caller's org — which is exactly a roster teacher whose
            // membership is still pending — and the panel then opened on an EMPTY conversation
            // list with nothing to explain it. The admin had done everything right and the product
            // simply said nothing.
            //
            // The fallback to the list STAYS: she can still reach a colleague by name, and the
            // panel being open is better than it vanishing. What changes is that she is told why
            // the thread did not open.
            //
            // ⚑ NO NEW COPY. `apiErrorMessage` resolves the server's own reason code first and
            // otherwise a translated status line that already ships in both bundles, so this reads
            // correctly in Hebrew without my inventing a `chat.openFailed` string — there is none
            // in either bundle, and the words are not mine to write.
            setView("list");
            toast.error(apiErrorMessage(error));
          });
      }),
    [setOpen, queryClient],
  );

  const close = () => setOpen(false);

  // Dismiss the pop-up via Esc + click-outside (reusing the Calendar/Mira pattern). The listeners
  // attach directly (no rAF/timeout deferral): this effect runs after commit — i.e. after the
  // opening click's dispatch has fully completed — so the listener can never catch the click that
  // opened the panel. A deferral would only reintroduce a timing race (the outside click can land
  // before a deferred bind, as it did in CI's slower jsdom).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) close();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  if (!myId) return null; // unauthenticated -> no chat surface

  return (
    <>
      {open ? (
        <div
          ref={panelRef}
          data-testid="chat-panel"
          className="fixed bottom-[calc(7rem+env(safe-area-inset-bottom))] end-5 z-40 flex h-[660px] max-h-[calc(100vh-8rem-env(safe-area-inset-bottom))] w-[440px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl md:bottom-24 md:max-h-[calc(100vh-7rem)]"
        >
          {view === "thread" && activeId ? (
            <ChatThread
              conversationId={activeId}
              myId={myId}
              onBack={() => { setActiveId(null); setView("list"); }}
              onClose={close}
            />
          ) : view === "new" ? (
            <NewConversation
              onCancel={() => setView("list")}
              onCreated={(conv) => { setActiveId(conv.id); setView("thread"); }}
              onClose={close}
            />
          ) : (
            <ConversationList
              myId={myId}
              onNew={() => setView("new")}
              onOpen={(conv) => { setActiveId(conv.id); setView("thread"); }}
              onClose={close}
            />
          )}
        </div>
      ) : null}
    </>
  );
}

function PanelHeader({ title, left, right, onClose }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
      <div className="flex items-center gap-2">
        {left}
        <span className="font-semibold text-slate-900">{title}</span>
      </div>
      <div className="flex items-center gap-2">
        {right}
        {onClose ? (
          <button
            type="button"
            data-testid="chat-close"
            onClick={onClose}
            aria-label={t("chat.closeMessages")}
            className="text-slate-400 transition hover:text-slate-600"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ConversationList({ myId, onNew, onOpen, onClose }) {
  const { t } = useTranslation();
  const listQuery = useQuery({
    queryKey: ["chat-conversations"],
    queryFn: () => chatApi.conversations().then((res) => res.data),
    refetchInterval: LIST_POLL_MS,
    refetchOnWindowFocus: true,
  });
  const conversations = listQuery.data?.conversations || [];

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <PanelHeader
        title={t("chat.messagesTitle")}
        onClose={onClose}
        right={
          <button
            type="button"
            data-testid="chat-new-button"
            onClick={onNew}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-primary/90"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> {t("chat.newButton")}
          </button>
        }
      />
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-500" data-testid="chat-empty">
            {t("chat.emptyConversations")}
          </p>
        ) : (
          conversations.map((conv) => (
            <button
              key={conv.id}
              type="button"
              data-testid="chat-conversation-row"
              onClick={() => onOpen(conv)}
              className="block w-full border-b border-slate-100 px-4 py-3 text-start hover:bg-slate-50"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-900"><bdi>{conversationLabel(conv, myId, t)}</bdi></span>
                {conv.is_group ? <span className="text-[11px] uppercase tracking-wide text-slate-400">{t("chat.groupBadge")}</span> : null}
              </div>
              {conv.last_message ? (
                <p className="mt-0.5 truncate text-xs text-slate-500"><bdi>{conv.last_message.text}</bdi></p>
              ) : (
                <p className="mt-0.5 text-xs italic text-slate-400">{t("chat.noMessagesYetList")}</p>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function NewConversation({ onCancel, onCreated, onClose }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState([]); // contact ids
  const [title, setTitle] = useState("");

  const contactsQuery = useQuery({
    queryKey: ["chat-contacts"],
    queryFn: () => chatApi.contacts().then((res) => res.data),
  });
  const contacts = contactsQuery.data?.contacts || [];
  const isGroup = selected.length > 1;

  const createMutation = useMutation({
    mutationFn: () =>
      chatApi
        .createConversation({ participant_ids: selected, is_group: isGroup, title: isGroup ? title.trim() || null : null })
        .then((res) => res.data),
    onSuccess: (conv) => onCreated(conv),
  });

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <PanelHeader
        title={t("chat.newMessageTitle")}
        onClose={onClose}
        left={
          <button type="button" data-testid="chat-cancel-new" onClick={onCancel} aria-label={t("chat.back")} className="text-slate-500 hover:text-slate-700">
            <ArrowLeft className="h-5 w-5 rtl:-scale-x-100" aria-hidden="true" />
          </button>
        }
      />
      <div className="flex-1 space-y-1 overflow-y-auto px-2 py-2">
        {contacts.length === 0 ? (
          <p className="px-2 py-4 text-sm text-slate-500">{t("chat.emptyContacts")}</p>
        ) : (
          contacts.map((c) => (
            <label
              key={c.id}
              data-testid="chat-contact-row"
              className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={selected.includes(c.id)}
                onChange={() => toggle(c.id)}
                className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/40"
              />
              <span className="text-sm font-medium text-slate-800"><bdi>{c.name}</bdi></span>
              {c.tenant_role ? (
                <span className="text-[11px] uppercase tracking-wide text-slate-400"><bdi>{String(c.tenant_role).replace("_", " ")}</bdi></span>
              ) : null}
            </label>
          ))
        )}
      </div>
      {isGroup ? (
        <div className="border-t border-slate-200 px-3 py-2">
          <input
            data-testid="chat-group-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("chat.groupNamePlaceholder")}
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </div>
      ) : null}
      <div className="border-t border-slate-200 px-3 py-2">
        <button
          type="button"
          data-testid="chat-create-submit"
          onClick={() => createMutation.mutate()}
          disabled={selected.length === 0 || createMutation.isPending}
          className="w-full rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {isGroup ? t("chat.startGroup") : t("chat.startConversation")}
        </button>
      </div>
    </div>
  );
}

function ChatThread({ conversationId, myId, onBack, onClose }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [input, setInput] = useState("");

  /**
   * W13 (C69 F4) — the thread needs the conversation's PARTICIPANTS, for two of the row's claims:
   * the header names the other person, and a message names its sender. The messages endpoint
   * returns `sender_id` and nothing else about who that is.
   *
   * THE LIST ALREADY HOLDS THEM, so this reuses its KEY and its fetcher rather than asking for a
   * new endpoint. The same choice AdminTeacherOverview makes with the roster.
   *
   * ⚑ AND `staleTime` IS LOAD-BEARING, which I learned from my own assertion failing. Opening a
   * thread UNMOUNTS the list, so this is a fresh observer on that key — and with the default
   * `staleTime: 0` a new observer refetches on mount, which is a second request for data we were
   * just handed. `staleTime: LIST_POLL_MS` says what the surface already believes: data younger
   * than the list's own poll interval is fresh. The node asserts ONE conversations request.
   */
  const conversationsQuery = useQuery({
    queryKey: ["chat-conversations"],
    queryFn: () => chatApi.conversations().then((res) => res.data),
    refetchInterval: LIST_POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: LIST_POLL_MS,
  });
  const conversation =
    (conversationsQuery.data?.conversations || []).find((c) => c.id === conversationId) || null;

  /** A participant's own name, as the server returned it. Empty when we cannot resolve it. */
  const senderName = (senderId) =>
    ((conversation?.participants || []).find((p) => p.id === senderId) || {}).name || "";

  // ⚑ F-66 — THE STAMP MOVED OUT, it did not change. The teacher page's Messages tab now shows
  // the same thread, and two surfaces formatting the same `created_at` their own way is the drift
  // this file's header warned about. One helper, both callers: `lib/chatStamp`.
  const stamp = (iso) => messageStamp(iso, { i18n });

  const messagesQuery = useQuery({
    queryKey: ["chat-messages", conversationId],
    queryFn: () => chatApi.messages(conversationId).then((res) => res.data),
    refetchInterval: THREAD_POLL_MS, // POLLING that feels live
  });
  const messages = messagesQuery.data?.messages || [];

  const sendMutation = useMutation({
    mutationFn: (text) => chatApi.sendMessage(conversationId, text).then((res) => res.data),
    onSuccess: () => {
      setInput("");
      // Honest: show the message once the refetch confirms it persisted.
      queryClient.invalidateQueries({ queryKey: ["chat-messages", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
    },
  });

  const send = () => {
    const text = input.trim();
    if (!text || sendMutation.isPending) return;
    sendMutation.mutate(text);
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden" data-testid="chat-thread">
      {/* W13 — the header named nobody: it said "Conversation" over every thread, so a teacher
          with two threads open in turn could not tell them apart from the header. This is the
          LIST's own label helper, so the header and the row it was opened from agree by
          construction, and the generic title stays as the honest fallback for the one frame
          before the cache resolves. No new strings. */}
      <PanelHeader
        title={conversation ? conversationLabel(conversation, myId, t) : t("chat.conversationTitle")}
        onClose={onClose}
        left={
          <button type="button" data-testid="chat-back" onClick={onBack} aria-label={t("chat.back")} className="text-slate-500 hover:text-slate-700">
            <ArrowLeft className="h-5 w-5 rtl:-scale-x-100" aria-hidden="true" />
          </button>
        }
      />
      <div className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <p className="text-sm text-slate-400">{t("chat.threadEmpty")}</p>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              data-testid="chat-message"
              data-mine={m.sender_id === myId ? "true" : "false"}
              className={m.sender_id === myId ? "ms-auto max-w-[80%]" : "max-w-[80%]"}
            >
              {/* W13 — WHO AND WHEN. Every message was a bare bubble: a group thread gave no way
                  to tell two other people apart, and nothing said when anything was said.

                  MY OWN messages get the time and NO name. There is no approved word for "you" in
                  this namespace — the only "You" values in the bundles mean "the teacher" in
                  Hebrew — and inventing one is LANE-1's to write, not mine. The alignment and the
                  bubble colour already say which side a message is on, so a name there would add
                  a word without adding a fact.

                  `ms-auto` and `justify-end` are logical, not left/right: in Hebrew the whole row
                  mirrors with the thread. */}
              <p
                data-testid="chat-message-meta"
                className={
                  m.sender_id === myId
                    ? "mb-0.5 flex flex-wrap items-center justify-end gap-1.5 text-[11px] text-slate-400"
                    : "mb-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400"
                }
              >
                {m.sender_id === myId ? null : (
                  <bdi className="font-medium text-slate-500" data-testid="chat-message-sender">
                    {senderName(m.sender_id)}
                  </bdi>
                )}
                <time dateTime={m.created_at || undefined} data-testid="chat-message-time">
                  {stamp(m.created_at)}
                </time>
              </p>
              <div
                className={
                  m.sender_id === myId
                    ? "rounded-2xl bg-primary px-3 py-2 text-sm leading-6 text-white"
                    : "rounded-2xl bg-slate-100 px-3 py-2 text-sm leading-6 text-slate-800"
                }
              >
                {/* user-authored message text — bidi-isolated so mixed he/en renders correctly.
                    W13 gave it its own testid: the row now also carries a sender name and a time,
                    so "the message" needs a handle that is the TEXT and not the whole row. */}
                <bdi data-testid="chat-message-text">{m.text}</bdi>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="flex items-center gap-2 border-t border-slate-200 px-3 py-2">
        <input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder={t("chat.messagePlaceholder")}
          className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        />
        <button
          type="button"
          data-testid="chat-send"
          onClick={send}
          disabled={sendMutation.isPending}
          className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
          aria-label={t("chat.send")}
        >
          <Send className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export default ChatLauncher;
